import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import Auth from './pages/Auth';
import Events from './pages/Events';
import NewEvent from './pages/NewEvent';
import EventPhotos from './pages/EventPhotos';
import PublicGallery from './pages/PublicGallery';
import EventGallery from './pages/EventGallery';
import SharePhoto from './pages/SharePhoto';
import PublicRegistration from './pages/PublicRegistration';
import CameraRegistration from './pages/CameraRegistration';
import PhotographerPage from './pages/PhotographerPage';
import ManageUsers from './pages/ManageUsers';
import TeamManagement from './pages/TeamManagement';
import ManageClient from './pages/ManageClient';
import Help from './pages/Help';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Settings from './pages/Settings';
import CreateClient from './pages/CreateClient';
import Support from './pages/Support';
import AdminSetup from './pages/AdminSetup';
import AdminLogin from './pages/AdminLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import CameraSetup from './pages/CameraSetup';
import './App.css';
import './global-design-system.css';

// Protected route component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    // Save the intended destination
    const returnTo = location.pathname + location.search;
    return <Navigate to={`/?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
        <Routes>
          {/* Main login page - venue owners login here */}
          <Route path="/" element={<Auth />} />
          
          {/* Admin Routes (for paying customers) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/setup" element={<AdminSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Camera FTP Setup (public) */}
          <Route path="/camera-setup" element={<CameraSetup />} />
          <Route path="/admin/dashboard" element={<Dashboard />} />
          <Route path="/admin/clients" element={<Clients />} />
          <Route path="/admin/settings" element={<Settings />} />
          <Route path="/admin/create-client" element={<CreateClient />} />
          <Route path="/support" element={<Support />} />
          <Route
            path="/events"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/new"
            element={
              <ProtectedRoute>
                <NewEvent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/photos"
            element={
              <ProtectedRoute>
                <EventPhotos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/events/:eventId/users"
            element={
              <ProtectedRoute>
                <ManageUsers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <TeamManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:clientId/manage"
            element={
              <ProtectedRoute>
                <ManageClient />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Help />
              </ProtectedRoute>
            }
          />
          {/* Public gallery route - no auth required */}
          <Route path="/gallery/:token" element={<PublicGallery />} />
          {/* Full event gallery - no auth required */}
          <Route path="/events/:eventId/gallery" element={<EventGallery />} />
          {/* Share photo landing page */}
          <Route path="/share/:platform/:photoId/:userId/:eventId" element={<SharePhoto />} />
          <Route path="/share/:photoId/:userId/:eventId" element={<SharePhoto />} />
          <Route path="/share/:photoId/:userId" element={<SharePhoto />} />
          {/* Public registration route - no auth required */}
          <Route path="/register/:eventId" element={<PublicRegistration />} />
          {/* Photographer page - token-based auth */}
          <Route path="/photographer/:eventId" element={<PhotographerPage />} />
          {/* Camera photographer registration - no auth required */}
          <Route path="/camera-register/:eventId" element={<CameraRegistration />} />
        </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
